import * as THREE from 'three';
import { PF_HH, PF_HW } from '../sim/constants';
import { buildEnvironment } from './Environment';
import { LightPool } from './LightPool';

/** FOV vertical de 30° (PLAN.md §4.2).
 *
 *  Assez fermé pour que le raccourci de perspective soit nul sur le plan de jeu
 *  — tout y étant à Z = 0, l'échelle ne varie pas d'un bout à l'autre de
 *  l'écran — tout en laissant du volume au décor. Un FOV de 60-70° écraserait
 *  les distances en haut d'écran et rendrait l'anticipation des trajectoires
 *  impossible : c'est l'échec documenté de G-Darius, Thunder Force V et
 *  RayStorm. */
const FOV = 30;
const HALF_FOV_TAN = Math.tan((FOV * Math.PI) / 360);

export class Stage {
  readonly renderer: THREE.WebGLRenderer;
  readonly scene = new THREE.Scene();
  /** ⚠️ La scène OVERLAY — projectiles ennemis, vaisseau, point de hitbox.
   *  Rendue APRÈS toute la chaîne de post-processing, à la résolution native.
   *  C'est LA décision qui détermine si le jeu est lisible ou non
   *  (PLAN.md §4.4) : le bloom sur les balles ennemies est le péché mortel du
   *  shmup néon amateur. */
  readonly overlay = new THREE.Scene();
  readonly camera: THREE.PerspectiveCamera;
  readonly lights: LightPool;

  constructor(canvasParent: HTMLElement = document.body) {
    this.renderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: false,
      powerPreference: 'high-performance',
      stencil: false,
    });
    this.renderer.setClearColor(0x05070c, 1);
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;

    // ⚠️ NoToneMapping est OBLIGATOIRE dès qu'un EffectComposer entre en jeu
    // (PLAN.md §4.1, piège n°1) : sinon la scène est tonemappée une première
    // fois à l'écriture dans le render target, le headroom au-dessus de 1.0 est
    // écrasé, et toute la stratégie de bloom par valeurs est annulée en
    // silence. Le post-processing n'arrive qu'au J3, mais on prend l'habitude
    // maintenant plutôt que de déboguer un rendu plat dans deux mois.
    this.renderer.toneMapping = THREE.NoToneMapping;
    this.renderer.shadowMap.enabled = false;

    canvasParent.appendChild(this.renderer.domElement);

    this.camera = new THREE.PerspectiveCamera(FOV, 1, 100, 6000);

    /* Éclairage FIXE : une directionnelle et une ambiante, créées ici et jamais
       retirées. Le nombre de lumières est un `#define` — en ajouter ou en
       retirer une recompile TOUS les programmes de la scène (PLAN.md §4.6). */
    this.scene.add(new THREE.AmbientLight(0x8899bb, 1.5));
    const key = new THREE.DirectionalLight(0xffffff, 1.8);
    key.position.set(-0.4, 0.8, 1);
    this.scene.add(key);

    // Les trois PointLight du pool sont ajoutées maintenant, avec intensity=0.
    // Elles font donc partie du compte de lumières dès le warm-up, et allumer
    // une explosion ne recompilera jamais rien.
    this.lights = new LightPool(this.scene);

    // ⚠️ AVANT le warm-up : l'envMap est un `#define`, la poser après
    // invaliderait toutes les variantes déjà compilées.
    buildEnvironment(this.renderer, this.scene);

    this.resize();
    window.addEventListener('resize', () => this.resize());
  }

  /** Cadre la caméra pour que le terrain 900 × 1200 tienne TOUJOURS, y compris
   *  en portrait sur téléphone. On recule jusqu'à satisfaire la contrainte la
   *  plus exigeante des deux ; l'excédent devient de la marge décorative. */
  resize(): void {
    const w = window.innerWidth;
    const h = window.innerHeight;
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.setSize(w, h);

    const aspect = w / h;
    const dist = Math.max(PF_HH / HALF_FOV_TAN, PF_HW / (HALF_FOV_TAN * aspect));
    this.camera.aspect = aspect;
    this.camera.position.set(0, 0, dist);
    this.camera.updateProjectionMatrix();
  }

  /** Applique la secousse. Le déplacement porte sur la CAMÉRA et non sur les
   *  objets : décaler le monde ferait bouger le décor par rapport au HUD DOM,
   *  et surtout coûterait une écriture par entité au lieu d'une seule. */
  setShake(x: number, y: number, roll: number): void {
    this.camera.position.x = x;
    this.camera.position.y = y;
    this.camera.rotation.z = roll;
  }

  render(): void {
    this.renderer.render(this.scene, this.camera);
  }
}
